import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { theme } from '../../config/theme';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, duration);
  }, []);

  const success = useCallback((message) => showToast(message, 'success'), [showToast]);
  const error = useCallback((message) => showToast(message, 'error'), [showToast]);
  const warning = useCallback((message) => showToast(message, 'warning'), [showToast]);
  const info = useCallback((message) => showToast(message, 'info'), [showToast]);

  const contextValue = useMemo(() => ({
    success, error, warning, info
  }), [success, error, warning, info]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts }) => {
  const containerStyles = {
    position: 'fixed',
    top: theme.spacing.xl,
    right: theme.spacing.xl,
    zIndex: theme.zIndex.toast,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
  };

  return (
    <div style={containerStyles}>
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} />
      ))}
    </div>
  );
};

const Toast = ({ message, type }) => {
  const colors = {
    success: theme.colors.success,
    error: theme.colors.error,
    warning: theme.colors.warning,
    info: theme.colors.info,
  };

  const toastStyles = {
    backgroundColor: theme.colors.background.paper,
    color: theme.colors.text.primary,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.lg,
    minWidth: '300px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    borderLeft: `4px solid ${colors[type]}`,
    animation: 'slideIn 0.3s ease-out',
  };

  const iconStyles = {
    width: '24px',
    height: '24px',
    color: colors[type],
  };

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  return (
    <div style={toastStyles}>
      <div style={iconStyles}>{icons[type]}</div>
      <div>{message}</div>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};